﻿import { useState, useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import {
  addOptionalField,
  updateFields,
} from "@/features/mapSchema/mapSchemaSlice";
import {
  Typography,
  Container,
  Box,
  Stack,
  IconButton,
  InputLabel,
  Input,
  Button,
  Select,
  MenuItem,
  FormControl,
} from "@mui/material";
import axios from "@/features/axiosHelper";
import {
  ShepDataGridPro,
  ShepCheckbox,
  ShepPagination,
} from "@/components/GenericComponents/ShepComponents";
import { createAlert } from "@/features/homepage/alertSliceList";
import DeleteIcon from "@/assets/img/icons/Delete/Line.svg?react";
import _ from "lodash";
import { ArtifactTypes } from "@/utils/utils";
import type { IMapSchemaField, IOptionalFieldDto } from "@/features/sharedTypes";
import type { GridRenderCellParams } from "@mui/x-data-grid";

interface IIsOptionalFieldsGrid {
  mappingArea: number;
  selectedOptionalRows: string[];
  artifactType: number;
  rowSelectionModel: string[];
  setRowSelectionModel: (data: string[]) => void;
}
interface IOptionalField {
  id: string;
  name: string;
  path: string;
  type: string;
}

const OptionalFieldsGrid = ({ 
  mappingArea, 
  selectedOptionalRows, 
  artifactType,
  rowSelectionModel,
  setRowSelectionModel,
}: IIsOptionalFieldsGrid) => {
  const dispatch = useDispatch();

  
  const packStructureLevels = ["Pallet", "Carton", "Item"];
  const [levelOption, setLevelOption] = useState("");

  const [newName, setNewName] = useState("");
  const [optionalFields, setOptionalFields] = useState<IOptionalField[]>([]);

  useEffect(() => {
    if (
      selectedOptionalRows &&
      selectedOptionalRows.length > 0 &&
      rowSelectionModel.length === 0 &&
      optionalFields.length > 0
    ) {
      setRowSelectionModel([selectedOptionalRows[0]]);
    }
  }, [selectedOptionalRows, optionalFields, selectedOptionalRows.length, rowSelectionModel.length]); // Update only when selectedRows changes

  // The 'RefreshOptionalFieldList' function makes the dependencies of useEffect Hook (at line 91) change on every render. To fix this, wrap the definition of 'RefreshOptionalFieldList' in its own useCallback() Hook 
  const RefreshOptionalFieldList = useCallback(() => {
    axios
      .get("/mapschema/artifact-optional-field", {
        params: { artifactType: artifactType, detailLevel: mappingArea },
      })
      .then((response) => {
        const data = response.data.map((x: IOptionalFieldDto) : IOptionalField => {
          const detail = JSON.parse(x.JSONDetails);
          return {
            id: x.ArtifactOptionalFieldId,
            name: detail.Name,
            path: detail.Path,
            type: "Text",
          };
        });
        setOptionalFields(data);
      })
      .catch((error) => {
        console.error("Error fetching data: ", error);
      });
  }, [artifactType, mappingArea]);

  useEffect(() => {
    RefreshOptionalFieldList();
  }, [artifactType, mappingArea, RefreshOptionalFieldList]);

  const AddNewOptionalField = () => {
    let fullPath = "";

    if (mappingArea === 0) {
      fullPath = "DocumentHeader.OptionalFields." + newName;
    } else if (
      artifactType == ArtifactTypes.PackedOrder &&
      levelOption !== ""
    ) {
      fullPath = `DocumentDetails.${levelOption}.OptionalFields.${newName}`;
    } else {
      // Fallback for empty selection or other artifact types
      fullPath = "DocumentDetails.OptionalFields." + newName;
    }

    const isDuplicate = optionalFields.some((x) => x.path === fullPath);
    if (isDuplicate) {
      dispatch(
        createAlert({
          status: "Warning",
          alertType: "result",
          message: `A Custom Field named ${newName} already exists at this specific mapping level.`,
          buttonLabel: "Close",
        }),
      );
      return;
    }

    if (newName.indexOf(" ") > -1) {
      dispatch(
        createAlert({
          status: "Warning",
          alertType: "result",
          message: `Custom Field ${newName} can not contain spaces. Please avoid special characters in general`,
          buttonLabel: "Close",
        }),
      );
      return;
    }
    if (newName.indexOf(".") > -1) {
      dispatch(
        createAlert({
          status: "Warning",
          alertType: "result",
          message: `Custom Field ${newName} can not contain periods. Please avoid special characters in general`,
          buttonLabel: "Close",
        }),
      );
      return;
    }

    axios
      .post(
        "/mapschema/add-new-artifact-optional-field",
        {
          Name: newName,
          Path: fullPath,
        },
        {
          params: {
            artifactType: artifactType,
            detailLevel: mappingArea,
          },
        },
      )
      .then((response) => {
        RefreshOptionalFieldList();
        axios
          .get("/mapschema/artifact-optional-field-by-id", {
            params: {
              artifactOptionalFieldId: response.data.ArtifactOptionalFieldId,
            },
          })
          .then((response) => {
            const data: IOptionalField[] = response.data.map((x: IOptionalFieldDto) : IMapSchemaField => {
              const detail = JSON.parse(x.JSONDetails);
              return {
                id: x.ArtifactOptionalFieldId,
                field: detail.Name,
                type: "String",
                maxLength: 0,
                isOptional: true,
              };
            });
            dispatch(addOptionalField(data));
          });
        dispatch(
          createAlert({
            status: "Success",
            alertType: "result",
            message: "Custom Field added successfully",
            buttonLabel: "Close",
          }),
        );
      })
      .catch((error) => console.error(error));
  };

  const columns = [
    {
      field: "name",
      headerName: "Custom Field Name",
      width: 450,
    },
    {
      field: "path",
      headerName: "Mapping Path",
      flex: 1,
    },
    {
      field: "type",
      headerName: "Type",
      width: 450,
    },
    {
      field: "actions",
      //type: "actions",
      headerName: "actions",
      width: 60,
      renderCell: (params: GridRenderCellParams) => {
        return (
        <IconButton onClick={() => handleDeleteRow(params.row)}>
          <DeleteIcon />
        </IconButton>
      )},
      sortable: false,
      filterable: false,
      //resizable: false,
    },
  ];

  const handleRowSelection = (selectionModel: string []) => {
    if (selectionModel.length > 0) {
      selectionModel = [_.last(selectionModel) as string] // select only one
      setRowSelectionModel(selectionModel);
      const selectedData = optionalFields.find(
        (row) => row.id === selectionModel[0],
      );
      if (!selectedData) {
        return;
      }
      dispatch(updateFields({ key: "targetField", value: selectedData.path }));
      dispatch(
        updateFields({
          key: "targetFieldIsOptional",
          value: true,
        }),
      );
    } else {
      return; // do not allow deselecting a row manually
    }
  };

  const handleDeleteRow = (row : IOptionalField) => {
    dispatch(
      createAlert({
        status: "Warning",
        alertType: "result",
        title: "This Custom Field will be deleted",
        message:
          "Are you sure you want to delete this record? Mappings with this custom field will not be deleted and may become invalid.",
        buttonLabel: "Yes",
        buttonLabel2: "No",
        onButtonClick: () => deleteCustomField(row),
      }),
    );
  };
  const deleteCustomField = async (row: IOptionalField ) => {
    axios
      .delete("/mapschema/artifact-optional-field", {
        params: {
          ids: JSON.stringify([row.id]),
        },
      })
      .then(() => {
        dispatch(
          createAlert({
            status: "Success",
            alertType: "single",
            message: "Custom field deleted successfully!",
          }),
        );
        RefreshOptionalFieldList();
      })
      .catch(() => {
        dispatch(
          createAlert({
            status: "Error",
            alertType: "single",
            message: "Error deleting custom field",
          }),
        );
      });
  };

  return (
    <Container>
      <Box sx={{ marginTop: 10 }}>
        <Typography variant="h3" sx={{ marginBottom: 2 }}>
          Add Custom Fields
        </Typography>
        {optionalFields.length > 0 ? (
          <ShepDataGridPro
            isDataLoaded={true}
            rows={optionalFields}
            columns={columns}
            getRowId={(row: IOptionalField) => row.id}
            columnHeaderHeight={80}
            rowHeight={80}
            pagination
            initialState={{
              pagination: {
                paginationModel: { pageSize: 5, page: 0 },
              },
              pinnedColumns: {
                right: ["actions"],
              },
            }}
            whiteHeader={true}
            checkboxSelection={true}
            disableMultipleRowSelection={false}
            disableColumnMenu={true}
            disableColumnFilter={true}
            disableColumnReorder={true}
            hideFooterSelectedRowCount={true}
            columnVisibilityModel={{
              endpointSchemaId: false,
            }}
            rowSelectionModel={rowSelectionModel}
            onRowSelectionModelChange={handleRowSelection}
            disableRowSelectionOnClick={true}
            slots={{
              baseCheckbox: ShepCheckbox,
              pagination: ShepPagination,
            }}
            sx={{
              color: "var(--text-primary)",
              fontSize: "var(--font-sm)",
              fontFamily: "var(--font-family)",
              borderTopLeftRadius: "44px",
              borderTopRightRadius: "44px",
              backgroundColor: "var(--default-light)",
              border: "none",
              "& .MuiDataGrid:hover": {
                backgroundColor: "inherit",
              },
              "& .MuiDataGrid-cellCheckbox, & .workflow-row": {
                opacity: 0,
                borderBottom: "none",
              },
              "& .MuiDataGrid-cell": {
                borderBottom: "none",
              },
              "& .MuiDataGrid-row:hover": {
                backgroundColor: "var(--tertiary-13)",
              },
              "& .Mui-selected": {
                backgroundColor: "var(--primary-pale)",
              },
              "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader.MuiDataGrid-columnHeaderCheckbox":
                {
                  outline: "none",
                },
              "& .MuiDataGrid-row:hover .MuiDataGrid-cellCheckbox, & .MuiDataGrid-row:hover .workflow-row":
                {
                  opacity: 1,
                },
              "& .MuiDataGrid-cellCheckbox": {
                borderLeft: "8px solid transparent",
              },
              "& .Mui-selected .MuiDataGrid-cellCheckbox": {
                opacity: 1,
                borderLeft: "8px solid var(--primary-main)",
                borderTopLeftRadius: "4px",
                borderBottomLeftRadius: "4px",
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                fontSize: "var(--font-md)",
                fontWeight: "var(--font-bold)",
                borderLeft: "none",
              },
              "& .MuiDataGrid-columnHeaders, & .MuiDataGrid-columnHeader:hover":
                {
                  borderBottomLeftRadius: "4px",
                  borderBottomRightRadius: "4px",
                  backgroundColor: "var(--primary-pale)",
                  borderBottom: "none",
                  borderLeft: "none",
                },
              "& .MuiDataGrid-pinnedColumnHeaders.MuiDataGrid-pinnedColumnHeaders--right":
                {
                  display: "none",
                },
              "& .MuiDataGrid-columnHeader[data-field='ErpConnectionName']": {
                paddingLeft: "45px",
              },
              "& .MuiDataGrid-cell[data-field='ErpConnectionName']": {
                paddingLeft: "45px",
              },
            }}
          />
        ) : (
          <Typography variant="h6" sx={{ textAlign: "center", mt: 2 }}>
            No data available
          </Typography>
        )}

        <Box sx={{ marginTop: 2 }}>
          {artifactType === ArtifactTypes.PackedOrder && mappingArea === 1 && (
            <Stack
              flexDirection={"row"}
              alignItems={"center"}
              style={{ margin: 10, marginBottom: 1 }}
            >
              <InputLabel id="level-option-label" style={{ marginRight: 46 }}>
                Select Level:
              </InputLabel>
              <FormControl variant="standard" sx={{ minWidth: 200 }}>
                <Select
                  labelId="level-option-label"
                  value={levelOption}
                  displayEmpty
                  onChange={(e) => {
                    setLevelOption(e.target.value);
                  }}
                  MenuProps={{
                    disablePortal: true,
                  }}
                  renderValue={(selected) => {
                    if (selected.length === 0) {
                      return (
                        <em style={{ color: "gray" }}>
                          Select a packing structure level
                        </em>
                      );
                    }
                    return selected;
                  }}
                >
                  <MenuItem value="">
                    <em style={{ color: "gray" }}>Select a level (Optional)</em>
                  </MenuItem>
                  {packStructureLevels.map((level) => (
                    <MenuItem key={level} value={level}>
                      {level}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          )}

          <Stack
            flexDirection={"row"}
            justifyContent={"space-between"}
            style={{ marginTop: 10 }}
          >
            <InputLabel style={{ margin: 10 }}>
              New Custom Field Name:
              <Input
                style={{ marginLeft: 46 }}
                onChange={(e) => setNewName(e.target.value)}
                value={newName}
              />
            </InputLabel>
            <Button
              className="btn-secondary"
              onClick={AddNewOptionalField}
              disabled={newName.length == 0}
              style={{ width: 180 }}
            >
              Add
            </Button>
          </Stack>
        </Box>
      </Box>
    </Container>
  );
};
export default OptionalFieldsGrid;
